import { Destructable, DestructionObserver, Subscription } from "../../destructables";
import { ScalarProvider, ScalarConsumer, Scalar, scalarValue } from "./Scalar";
import { getSourceOriginFromStacktrace } from "./ExpressionBuilder";

export type ReadonlyVariable<ValueType> = ScalarProvider<ValueType> & Destructable;

export class MutableVariable<ValueType>
	extends ScalarProvider<ValueType>
	implements ScalarConsumer<ValueType>, Destructable
{
	private _value: Scalar<ValueType>;
	private readonly _subscribers = new Set<ScalarConsumer<ValueType>>();

	constructor(
		private readonly expression: Scalar<ValueType>,
		private _on_abandoned: (() => void) | undefined = undefined,
		readonly __origin?: string
	) {
		super();
		this._value = expression;
	}

	set(value: ValueType) {
		if (this._destroyed) throw new Error("Trying to access destroyed variable");
		if (this._value instanceof ScalarProvider && this._subscribers.size > 0) {
			this._value.unsubscribe(this);
		}
		this._value = value;
		this.update(value);
	}

	ensure(value: ValueType) {
		if (this._destroyed) throw new Error("Trying to access destroyed variable");
		if (this._value === value) return;
		this.set(value);
	}

	get value() {
		if (this._destroyed) throw new Error("Trying to access destroyed variable");
		return scalarValue(this._value);
	}

	update(value: ValueType) {
		for (const subscriber of this._subscribers) {
			subscriber.update(value);
		}
	}

	discard() {
		if (this._destroyed) throw new Error("Trying to access destroyed variable");
		if (this._value === this.expression) return;
		this._value = this.expression;
		if (this._subscribers.size > 0) {
			if (this._value instanceof ScalarProvider) {
				this._value.subscribe(this);
			} else {
				this.update(this._value);
			}
		}
	}

	subscribe(subscriber: ScalarConsumer<ValueType>) {
		if (this._destroyed) throw new Error("Trying to access destroyed variable");
		if (this._subscribers.has(subscriber)) throw new Error("unexpected");
		this._subscribers.add(subscriber);
		if (this._subscribers.size === 1 && this._value instanceof ScalarProvider) {
			this._value.subscribe(this);
		} else {
			subscriber.update(this.value);
		}
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<ValueType>) {
		if ((<any>global)?.alan_strict && !this._subscribers.has(subscriber)) throw new Error("unexpected");
		this._subscribers.delete(subscriber);
		if (this._value instanceof ScalarProvider && this._subscribers.size === 0) {
			this._value.unsubscribe(this);
			this._on_abandoned?.();
		}
	}

	/* Destructable interface implementation */

	private _destruction_observers?: Set<DestructionObserver>;
	private _destroyed = false;

	destroy(): void {
		if (this._destroyed) return;
		this._destroyed = true;

		if (this._subscribers.size > 0) {
			this._subscribers.clear();
			if (this._value instanceof ScalarProvider) {
				this._value.unsubscribe(this);
			}
		}

		if (this._destruction_observers) {
			for (const observer of this._destruction_observers) {
				observer();
			}
			this._destruction_observers.clear();
		}
	}

	get destroyed(): boolean {
		return this._destroyed;
	}

	onDestroy(observer: DestructionObserver): Subscription {
		if (this.destroyed) {
			observer();
		} else {
			(this._destruction_observers ??= new Set()).add(observer);
		}
		return {
			unsubscribe: () => this._destruction_observers!.delete(observer),
		};
	}
}

export function createVariable<ValueType>(
	expression: Scalar<ValueType>,
	mutable: false,
	on_abandoned?: () => void
): ReadonlyVariable<ValueType>;
export function createVariable<ValueType>(
	default_value: Scalar<ValueType>,
	mutable: true,
	on_abandoned?: () => void
): MutableVariable<ValueType>;
export function createVariable<ValueType>(
	expression: Scalar<ValueType>,
	mutable: boolean,
	on_abandoned: (() => void) | undefined
): MutableVariable<ValueType> {
	const origin = (<any>global)?.alan_debug ? getSourceOriginFromStacktrace(new Error(), 2) : undefined;
	return new MutableVariable(expression, on_abandoned, origin);
}
